import React from 'react';
import { GetStaticProps } from 'next';
import { fetchAPI, getPrimaryMenuItems } from '../lib/api';
import { IMenuItem } from '../models/menu-item';
import config from '../lib/config';
import { ISponsor } from '../models/sponsor';
import { IPage } from '../models/page';
import Container from '../components/container';
import Layout from '../components/layout';
import NavBar from '../components/navbar';
import { Text, H2, H3, Label, P } from '../components/text';

import { Tab } from '@headlessui/react';
import classNames from 'classnames';
import PostBody from '@/components/post-body';

// Add to a types file or top of activatie.tsx
export interface ActivatieItem {
	title: string;
	image: {
		sourceUrl: string;
		altText: string;
	};
	name: string;
	value?: string;
	url?: string; // Optional URL for items that might link to more details
}
export interface ActivatieCategorie {
	fieldGroupName: string; // e.g. 'ActivatiePeriodes_Periodes_Categorieen_Stemweekenmaand'
	items: ActivatieItem[];
}

export interface ActivatiePeriode {
	periodeTitel: string;
	categorieen: ActivatieCategorie[];
}
export interface ActivatiePeriodesData {
	periodes: ActivatiePeriode[];
}

interface IProps {
	primaryMenuItems: IMenuItem[];
	sponsors: ISponsor[];
	pageData: IPage;
}

export default function Activatie({ pageData: { page }, primaryMenuItems, sponsors }: IProps) {
	const pageTitle = `${page.title} | ${config.siteName}`;
	const meta = page.seo?.description || page.title;

	const activatiePeriodes: ActivatiePeriodesData | undefined = page.activatiePeriodes;

	if (!activatiePeriodes || !activatiePeriodes.periodes?.length) {
		return (
			<Layout preview sponsors={sponsors} metaDesc={meta} metaTitle={pageTitle}>
				<NavBar items={primaryMenuItems} />
				<Container>
					<H2>Geen activatie periodes gevonden.</H2>
				</Container>
			</Layout>
		);
	}

	return (
		<Layout preview sponsors={sponsors} metaDesc={meta} metaTitle={pageTitle}>
			<NavBar items={primaryMenuItems} />
			<div className="w-full h-full relative">
				<div className="px-6 sm:pb-0 sm:pt-12 lg:px-8">
					<div className="mx-auto max-w-6xl text-center">
						<Text
							as="h2"
							className="text-2xl md:text-4xl font-bold lg:text-5xl text-center pt-16 pb-12 lg:py-8"
						>
							{page.title}
						</Text>
						<P className="max-w-4xl mx-auto text-center py-4 lg:pb-8">
							Op deze pagina kan je terecht voor alle verkiezingen van de Keuken Kampioen Divisie! Hier
							stem je op de Veldsink Advies Speler van de Week, Bronzen Schilden & het Doelpunt van de
							Periode.
						</P>
						<div className="max-w-4xl mx-auto text-left">
							{page.content && <PostBody content={page.content} />}
						</div>
					</div>
				</div>
				<div className="w-full sm:px-0 mx-auto">
					<Container>
						<Tab.Group>
							<Tab.List
								className={classNames(
									'border-b',
									'border-red-500',
									'justify-center',
									activatiePeriodes.periodes.length > 3 ? 'block lg:flex' : 'flex',
								)}
							>
								{activatiePeriodes.periodes.map((periode) => (
									<Tab
										key={periode.periodeTitel}
										className={({ selected }) =>
											classNames(
												'tab-lg w-full lg:w-auto -translate-y-1',
												'ring-0 focus:ring-0 focus:ring-offset-0 focus:outline-none',
												selected
													? 'text-white opacity-100 border-red-500 border-b-4 hover:opacity-80'
													: 'text-black-500 opacity-50 hover:opacity-100',
											)
										}
									>
										<Label>{periode.periodeTitel}</Label>
									</Tab>
								))}
							</Tab.List>
							<Tab.Panels className="">
								{activatiePeriodes.periodes.map((periode, idx) => (
									<Tab.Panel key={idx}>
										{periode.categorieen?.map((categorie, groupIdx) => (
											<div
												key={groupIdx}
												className={classNames(
													'mb-8 p-4 sm:p-8 rounded-md shadow -mt-2',
													categorie.fieldGroupName.endsWith('Topspelers')
														? 'bg-red-500'
														: 'bg-white',
												)}
											>
												{/* Render items as before, based on group type */}
												{/* Example for Topspelers */}
												{categorie.fieldGroupName.endsWith('Topspelers') ? (
													<>
														<div className="text-center mb-4 max-w-2xl mx-auto">
															<H3 className="mb-3 text-white">Genomineerden</H3>
														</div>
														<div className="flex flex-wrap justify-center gap-4">
															{Array.isArray(categorie.items) &&
																categorie.items.map((item, itemIdx) => (
																	<a
																		href={item.url || '#'}
																		key={itemIdx}
																		className="bg-white text-center rounded-lg shadow px-6 py-6 flex flex-col gap-y-3 items-center space-x-2 hover:shadow-lg transition"
																	>
																		<img
																			src={item.image.sourceUrl}
																			alt={item.image.altText}
																			className="w-24 h-24 object-cover rounded-full mb-2"
																		/>
																		<div>
																			<Label className="!text-red-500">
																				{item.title}
																			</Label>
																			<P>{item.name}</P>
																			<P className="!font-bold">{item.value}</P>
																		</div>
																	</a>
																))}
														</div>
													</>
												) : categorie.fieldGroupName.endsWith('Stemweekenmaand') ? (
													<div className="flex flex-col items-center gap-4">
														<div className="flex justify-center gap-4 w-full">
															{Array.isArray(categorie.items) &&
																categorie.items.map((item, itemIdx) => (
																	<a
																		key={itemIdx}
																		href={item.url || '#'}
																		className="relative w-full max-w-md aspect-video rounded-lg shadow hover:shadow-lg transition overflow-hidden"
																	>
																		<img
																			src={item.image.sourceUrl}
																			alt={item.image.altText}
																			className="object-cover w-full h-full"
																		/>
																		<div className="absolute bottom-0 left-0 p-4">
																			<h4 className="text-white text-md font-semibold">
																				{item.title}
																				{item.name && ` - ${item.name}`}
																			</h4>
																		</div>
																	</a>
																))}
														</div>
													</div>
												) : categorie.fieldGroupName.endsWith('Stemperiode') ? (
													<div className="flex justify-center">
														{Array.isArray(categorie.items) &&
															categorie.items.map((item, itemIdx) => (
																<a
																	href={item.url || '#'}
																	key={itemIdx}
																	className="relative w-full max-w-2xl aspect-[32/9] rounded-lg shadow hover:shadow-lg transition overflow-hidden"
																>
																	<img
																		src={item.image.sourceUrl}
																		alt={item.image.altText}
																		className="object-cover w-full h-full"
																	/>
																	<div className="absolute bottom-0 left-0 p-4">
																		<h4 className="text-white text-md font-semibold">
																			{item.title}
																			{item.name && ` - ${item.name}`}
																		</h4>
																	</div>
																</a>
															))}
													</div>
												) : (
													<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
														{categorie.items.map((item, itemIdx) => (
															<div
																key={itemIdx}
																className="relative rounded-lg shadow overflow-hidden hover:shadow-lg transition aspect-video"
															>
																<img
																	src={item.image.sourceUrl}
																	alt={item.image.altText}
																	className="object-cover w-full h-full"
																/>
																<div className="absolute bottom-0 left-0 p-4">
																	<h4 className="text-white text-md font-semibold">
																		{item.title}
																		{item.name && ` - ${item.name}`}
																	</h4>
																</div>
															</div>
														))}
													</div>
												)}
											</div>
										))}
									</Tab.Panel>
								))}
							</Tab.Panels>
						</Tab.Group>
					</Container>
				</div>
			</div>
		</Layout>
	);
}

// ...existing code...
export const getStaticProps: GetStaticProps = async ({ preview = false }) => {
	const primaryMenuItems = await getPrimaryMenuItems();
	const pageData = await fetchAPI<{ sponsors: { nodes: any[] }; page: any }>(`
    query {
        page(id: 137206, idType: DATABASE_ID) {
            title
            content
            activatiePeriodes {
                periodes {
                    periodeTitel
                    categorieen {
                        ... on Page_Activatieperiodes_periodes_Categorieen_Stemweekenmaand {
                            fieldGroupName
                            items {
                                title
                                image {
									sourceUrl
									altText
								}
                                name
								url
                            }
                        }
                        ... on Page_Activatieperiodes_periodes_Categorieen_Topspelers {
                            fieldGroupName
                            items {
                                title
                                image {
									sourceUrl
									altText
								}
                                name
                                value
								url
                            }
                        }
                        ... on Page_Activatieperiodes_periodes_Categorieen_Stemperiode {
                            fieldGroupName
                            items {
                                title
                                image {
									sourceUrl
									altText
								}
                                name
								url
                            }
                        }
                    }
                }
            }
        }
        sponsors(first: 20) {
            nodes {
                title
                sponsor_fields {
                    subtitle
                    websiteUrl
                    mainSponsor
                    invertedImage {
                        sourceUrl
                    }
                }
            }
        }
    }
`);

	if (!pageData.page) {
		return { notFound: true };
	}

	return {
		revalidate: 3600,
		props: {
			sponsors: pageData.sponsors.nodes,
			pageData,
			preview,
			primaryMenuItems,
		},
	};
};
